import type { AxiosError } from 'axios';
import type { UseMutationResult } from 'react-query';
import { useMutation } from 'react-query';

import {
  HIGHLIGHTS_LIST_QUERY_KEY,
  HEATMAP_HIGHLIGHTS_LIST_QUERY_KEY,
  UNSEEN_HIGHLIGHTS_COUNT,
  createHeatmapHighlight,
} from '@hotjar/webapp/domains/highlights/api';
import * as collectionsApi from '@hotjar/webapp/domains/collections/api';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { event } from '@hotjar/webapp/tracking/tools/hotjar';
import { mark, measureAndLog } from '@hotjar/performance';
import { MARKS } from '@hotjar/webapp/performance';

type ApiFunction = typeof createHeatmapHighlight;

const HEATMAP_HIGHLIGHT_CREATED = 'heatmap_highlight_created';

export function useHeatmapHighlightCreate(
  siteId: Parameters<ApiFunction>[0] | null,
): UseMutationResult<Awaited<ReturnType<ApiFunction>>, AxiosError, Parameters<ApiFunction>[1]> {
  return useMutation(
    async (highlightData) => {
      if (siteId === null) {
        throw new Error('site ID is not defined');
      }

      // Perf: Mark create heatmap highlight timings
      mark(MARKS.HEATMAP_HIGHLIGHT_CREATE_START);

      return createHeatmapHighlight(siteId, highlightData);
    },
    {
      onSuccess: () => {
        event(HEATMAP_HIGHLIGHT_CREATED);

        queryClient.invalidateQueries(HIGHLIGHTS_LIST_QUERY_KEY);
        queryClient.invalidateQueries(HEATMAP_HIGHLIGHTS_LIST_QUERY_KEY);
        queryClient.invalidateQueries(UNSEEN_HIGHLIGHTS_COUNT);
        queryClient.invalidateQueries(collectionsApi.COLLECTIONS_LIST_QUERY_KEY);
      },
      onSettled: () => {
        // Perf: Mark create heatmap highlight timings
        measureAndLog({
          name: MARKS.HEATMAP_HIGHLIGHT_CREATE_TIME,
          startMark: MARKS.HEATMAP_HIGHLIGHT_CREATE_START,
        });
      },
    },
  );
}
